import React, { useState, useCallback, useMemo } from "react";
import { View } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { extractApiError } from "@/lib/api/errors";
import { Modal } from "@/components/ui/Modal";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { SelectField } from "@/components/ui/SelectField";
import { useI18n } from "@/context/I18nContext";
import { invitationsApi } from "@/lib/api/invitations";
import type { SupportedLocale } from "@chinese-vendor/i18n";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";

type State = "confirmation" | "success" | "error";

export interface ResendInviteModalProps {
  visible: boolean;
  /** Email address of the invite being resent — displayed in the confirmation body. */
  email: string;
  /** ID of the invitation to resend. */
  invitationId: string;
  onDismiss: () => void;
  /** Called after a successful resend so the list/detail can refresh. */
  onResent?: () => void;
}

export function ResendInviteModal({
  visible,
  email,
  invitationId,
  onDismiss,
  onResent,
}: ResendInviteModalProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<State>("confirmation");
  const [error, setError] = useState<string | null>(null);
  const [locale, setLocale] = useState<SupportedLocale>("en");

  const localeOptions = useMemo(
    () => [
      { label: t("common.language.en"), value: "en" },
      { label: t("common.language.zh"), value: "zh-CN" },
      { label: t("common.language.ar"), value: "ar" },
    ],
    [t]
  );

  const resetForm = useCallback(() => {
    setState("confirmation");
    setSubmitting(false);
    setError(null);
    setLocale("en");
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
  }, [resetForm, onDismiss]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    try {
      await invitationsApi.resend(invitationId, { locale });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["invitations", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["invitations", "detail", invitationId] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
      ]);
      setState("success");
      onResent?.();
    } catch (err) {
      setError(extractApiError(err, t("common.error.generic")));
      setState("error");
    } finally {
      setSubmitting(false);
    }
  }, [invitationId, locale, queryClient, t, onResent]);

  const body = t("staff.invitations.resend.body").replace("{email}", email);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      title={state === "confirmation" ? "staff.invitations.resend.title" : null}
      closeOnBackdrop={!submitting}
      footer={
        state === "confirmation" ? (
          <View className="flex-row gap-3 justify-end">
            <Button
              label="staff.invitations.resend.cancel"
              onPress={handleDismiss}
              variant="tertiary"
              disabled={submitting}
            />
            <Button
              label="staff.invitations.resend.confirm"
              onPress={handleConfirm}
              variant="primary"
              loading={submitting}
            />
          </View>
        ) : null
      }
    >
      {state === "confirmation" && (
        <View className="gap-4 w-full">
          <AppText className="text-sm text-dark-text-30">{body}</AppText>
          <SelectField
            label="staff.invitations.resend.languageLabel"
            placeholder="staff.invitations.resend.languagePlaceholder"
            options={localeOptions}
            value={locale}
            onChange={(v) => setLocale(v as SupportedLocale)}
            required
          />
        </View>
      )}

      {state === "success" && (
        <SuccessState
          title="staff.invitations.resend.success"
          description="staff.invitations.resend.successBody"
          className="px-0 pt-0"
        />
      )}

      {state === "error" && (
        <ErrorState
          descriptionText={error || undefined}
          description="common.error.generic"
          className="px-0 pt-0"
        />
      )}
    </Modal>
  );
}
